/**
 * External system form component with Vuexy styling.
 * Provides form for creating and editing external systems with organized sections.
 */

// React Imports
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

// MUI Imports
import {
  Box,
  Typography,
  Button,
  FormControlLabel,
  Switch,
  Alert,
  CircularProgress,
  Grid,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Card,
  CardContent,
  IconButton,
} from '@mui/material';
import { Save as SaveIcon, ArrowBack as ArrowBackIcon } from '@mui/icons-material';

// Component Imports
import { TextField } from '@core/components/mui';
import { externalSystemService } from '../../services/externalSystemService';
import { useNotificationStore } from '../../store/notificationStore';

const AUTHENTICATION_TYPES = [
  { value: 'None', label: 'None' },
  { value: 'ApiKey', label: 'API Key' },
  { value: 'BearerToken', label: 'Bearer Token' },
  { value: 'BasicAuth', label: 'Basic Auth' },
  { value: 'OAuth2', label: 'OAuth 2.0' },
  { value: 'JWT', label: 'JWT' },
  { value: 'CustomHeader', label: 'Custom Header' },
  { value: 'RequestSigning', label: 'Request Signing' },
];

/**
 * External system form component for creating and editing external systems.
 */
export const ExternalSystemForm = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isEdit = !!id;
  const { addNotification } = useNotificationStore();

  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Form fields
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [baseUrl, setBaseUrl] = useState('');
  const [authenticationType, setAuthenticationType] = useState('None');
  const [authenticationConfigJson, setAuthenticationConfigJson] = useState('');
  const [defaultHeadersJson, setDefaultHeadersJson] = useState('');
  const [isActive, setIsActive] = useState(true);

  useEffect(() => {
    if (isEdit && id) {
      loadSystem();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, isEdit]);

  const loadSystem = async () => {
    if (!id) return;
    setLoading(true);
    setError(null);
    try {
      const data = await externalSystemService.getExternalSystem(id);
      setName(data.name);
      setDescription(data.description || '');
      setBaseUrl(data.baseUrl);
      setAuthenticationType(data.authenticationType);
      setAuthenticationConfigJson(''); // Don't load auth config for security
      setDefaultHeadersJson(data.defaultHeadersJson || '');
      setIsActive(data.isActive);
    } catch (err: any) {
      const msg = err.response?.data?.message || 'Failed to load external system';
      setError(msg);
      addNotification(msg, 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!name.trim()) {
      setError('Name is required');
      return;
    }

    if (!baseUrl.trim()) {
      setError('Base URL is required');
      return;
    }

    // Validate URL format
    try {
      new URL(baseUrl.trim());
    } catch {
      setError('Base URL must be a valid URL');
      return;
    }

    // Validate JSON fields if provided
    if (authenticationConfigJson.trim()) {
      try {
        JSON.parse(authenticationConfigJson.trim());
      } catch {
        setError('Authentication configuration JSON must be valid JSON');
        return;
      }
    }

    if (defaultHeadersJson.trim()) {
      try {
        JSON.parse(defaultHeadersJson.trim());
      } catch {
        setError('Default headers JSON must be valid JSON');
        return;
      }
    }

    setSaving(true);
    try {
      const data = {
        name: name.trim(),
        description: description.trim() || undefined,
        baseUrl: baseUrl.trim(),
        authenticationType,
        authenticationConfigJson: authenticationConfigJson.trim() || undefined,
        defaultHeadersJson: defaultHeadersJson.trim() || undefined,
        isActive,
      };

      if (isEdit && id) {
        await externalSystemService.updateExternalSystem(id, data);
        addNotification('External system updated successfully', 'success');
      } else {
        await externalSystemService.createExternalSystem(data);
        addNotification('External system created successfully', 'success');
      }
      navigate('/external-systems');
    } catch (err: any) {
      const msg = err.response?.data?.message || 'Failed to save external system';
      setError(msg);
      addNotification(msg, 'error');
    } finally {
      setSaving(false);
    }
  };

  const getAuthConfigHelperText = () => {
    switch (authenticationType) {
      case 'OAuth2':
        return 'JSON: { "clientId": "...", "clientSecret": "...", "tokenUrl": "...", "scope": "..." }';
      case 'JWT':
        return 'JSON: { "secret": "...", "issuer": "...", "audience": "..." }';
      case 'ApiKey':
        return 'JSON: { "key": "...", "location": "header|query", "headerName": "...", "queryParameterName": "..." }';
      case 'BasicAuth':
        return 'JSON: { "username": "...", "password": "..." }';
      case 'BearerToken':
        return 'JSON: { "token": "..." }';
      case 'CustomHeader':
        return 'JSON: { "headerName": "...", "headerValue": "..." }';
      case 'RequestSigning':
        return 'JSON: { "secret": "...", "signatureHeaderName": "..." }';
      default:
        return 'Authentication configuration JSON (optional)';
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Card>
        <CardContent sx={{ p: 6 }}>
          {/* Header */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 4 }}>
            <IconButton onClick={() => navigate('/external-systems')} sx={{ mr: -1 }}>
              <ArrowBackIcon />
            </IconButton>
            <Typography variant="h4" sx={{ fontWeight: 600, flex: 1 }}>
              {isEdit ? 'Edit External System' : 'Create External System'}
            </Typography>
          </Box>

          {/* Error Alert */}
          {error && (
            <Alert severity="error" sx={{ mb: 4 }} onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          {/* Form */}
          <form onSubmit={handleSubmit}>
            <Grid container spacing={4}>
            <Grid size={{ xs: 12 }}>
              <TextField
                label="Name"
                fullWidth
                required
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
            </Grid>

            <Grid size={{ xs: 12 }}>
              <TextField
                label="Description"
                fullWidth
                multiline
                rows={3}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
              />
            </Grid>

            <Grid size={{ xs: 12 }}>
              <TextField
                label="Base URL"
                fullWidth
                required
                value={baseUrl}
                onChange={(e) => setBaseUrl(e.target.value)}
                helperText="The base URL for the external API (e.g., https://api.example.com)"
              />
            </Grid>

            <Grid size={{ xs: 12 }}>
              <FormControl fullWidth>
                <InputLabel>Authentication Type</InputLabel>
                <Select
                  value={authenticationType}
                  onChange={(e) => setAuthenticationType(e.target.value)}
                  label="Authentication Type"
                >
                  {AUTHENTICATION_TYPES.map((type) => (
                    <MenuItem key={type.value} value={type.value}>
                      {type.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {authenticationType !== 'None' && (
              <Grid size={{ xs: 12 }}>
                <TextField
                  label="Authentication Configuration"
                  fullWidth
                  multiline
                  rows={6}
                  value={authenticationConfigJson}
                  onChange={(e) => setAuthenticationConfigJson(e.target.value)}
                  helperText={getAuthConfigHelperText()}
                />
              </Grid>
            )}

            <Grid size={{ xs: 12 }}>
              <TextField
                label="Default Headers JSON"
                fullWidth
                multiline
                rows={4}
                value={defaultHeadersJson}
                onChange={(e) => setDefaultHeadersJson(e.target.value)}
                helperText='JSON object with default headers (e.g., { "Accept": "application/json", "User-Agent": "CoOrdio Cortex/1.0" })'
              />
            </Grid>

            <Grid size={{ xs: 12 }}>
              <FormControlLabel
                control={<Switch checked={isActive} onChange={(e) => setIsActive(e.target.checked)} />}
                label="Active"
              />
            </Grid>

              <Grid size={{ xs: 12 }}>
                <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', mt: 6 }}>
                  <Button variant="outlined" onClick={() => navigate('/external-systems')}>
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    variant="contained"
                    startIcon={saving ? <CircularProgress size={20} /> : <SaveIcon />}
                    disabled={saving}
                  >
                    {saving ? 'Saving...' : isEdit ? 'Update' : 'Create'}
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </form>
        </CardContent>
      </Card>
    </Box>
  );
};
